import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Breadcrumb } from '@/components/ui/breadcrumb'
import { useState, useEffect } from 'react'
import { useToast } from '@/hooks/use-toast'
import { AlertTriangle, X, Lock, Trash2, Sparkles, Palette } from 'lucide-react'
import { getDropByIdWithAccess, updateDrop, deleteDrop, closeDrop, type Drop, type DropAccessError, type DropBranding } from '@/lib/dropsStore'
import { useAuth } from '@/contexts/AuthContext'
import { Link } from 'react-router-dom'
import { DEMO_AUTH_ENABLED } from '@/lib/env'
import { useDeleteDropMutation, useDropQuery, useUpdateDropMutation } from '@/data/drops'
import { PhotoSettings } from '@/components/PhotoSettings'
import { useDropPluginsQuery, useUpsertDropPluginMutation, useDeleteDropPluginMutation, type PluginSettings, type ExifExtractorSettings } from '@/data/dropPlugins'

export default function EditDropPage() {
  return DEMO_AUTH_ENABLED ? <DemoEditDropPage /> : <RealEditDropPage />
}

function DemoEditDropPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { organization } = useAuth()
  const [showCloseModal, setShowCloseModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [drop, setDrop] = useState<Drop | null>(null)
  const [accessError, setAccessError] = useState<DropAccessError | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [customThankYouMessage, setCustomThankYouMessage] = useState('')
  const [primaryColor, setPrimaryColor] = useState('')
  const [accentColor, setAccentColor] = useState('')
  const [logoUrl, setLogoUrl] = useState('')

  // Check if user has Pro or Business tier for advanced features
  const isPaidTier = organization?.tier === 'pro' || organization?.tier === 'business'

  // Load drop data on mount with access control
  useEffect(() => {
    if (id) {
      const result = getDropByIdWithAccess(id)
      if (result.error) {
        setAccessError(result.error)
        setDrop(null)
      } else if (result.drop) {
        setDrop(result.drop)
        setName(result.drop.name)
        setDescription(result.drop.description || '')
        setCustomThankYouMessage(result.drop.customThankYouMessage || '')
        setPrimaryColor(result.drop.branding?.primaryColor || '')
        setAccentColor(result.drop.branding?.accentColor || '')
        setLogoUrl(result.drop.branding?.logoUrl || '')
        setAccessError(null)
      }
    }
  }, [id])

  const dropName = drop?.name || 'Drop'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!id) return

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500))

    // Build branding object if paid tier
    const branding: DropBranding | undefined = isPaidTier && (primaryColor || accentColor || logoUrl)
      ? {
          primaryColor: primaryColor || undefined,
          accentColor: accentColor || undefined,
          logoUrl: logoUrl || undefined,
        }
      : undefined

    // Update the drop in the store
    const updatedDrop = updateDrop(id, {
      name: name.trim(),
      description: description.trim() || undefined,
      customThankYouMessage: isPaidTier ? (customThankYouMessage.trim() || undefined) : undefined,
      branding,
    })

    if (updatedDrop) {
      toast({
        title: 'Changes saved',
        description: 'Your drop has been updated successfully.',
      })
      navigate(`/drops/${id}`)
    } else {
      toast({
        title: 'Error',
        description: 'Failed to update drop.',
        variant: 'destructive',
      })
    }
  }

  const handleCloseDrop = async () => {
    if (!id) return

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500))

    const isFreeTier = organization?.tier === 'free'
    const closedDrop = closeDrop(id, isFreeTier)

    if (closedDrop) {
      toast({
        title: 'Drop closed',
        description: 'Contributors can no longer upload photos.',
      })
      setShowCloseModal(false)
      navigate(`/drops/${id}`)
    }
  }

  const handleDeleteDrop = async () => {
    if (!id) return

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500))

    const success = deleteDrop(id)

    if (success) {
      toast({
        title: 'Drop deleted',
        description: 'The drop has been permanently deleted.',
      })
      navigate('/drops')
    }
  }

  // Show access denied state (403)
    if (accessError?.code === 'ACCESS_DENIED') {
      return (
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center">
            <div className="h-16 w-16 bg-destructive/15 rounded-full flex items-center justify-center mx-auto">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <h1 className="mt-6 text-2xl font-bold">Access Denied</h1>
            <p className="mt-2 text-muted-foreground max-w-md mx-auto">
              You do not have permission to edit this drop. It may belong to a different organization.
            </p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Go Back
            </Button>
            <Button asChild>
              <Link to="/drops">View Your Drops</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  // Show not found state (404)
  if (accessError?.code === 'NOT_FOUND') {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="h-16 w-16 bg-muted rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="mt-6 text-2xl font-bold">Drop not found</h1>
          <p className="mt-2 text-muted-foreground max-w-md mx-auto">
            This drop may have been deleted or does not exist.
          </p>
          <div className="mt-6 flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Go Back
            </Button>
            <Button asChild>
              <Link to="/drops">View All Drops</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Drops', href: '/drops' },
          { label: dropName, href: `/drops/${id}` },
          { label: 'Edit' },
        ]}
      />

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold">Edit Drop</h1>
        <p className="text-muted-foreground">Update your drop settings</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-6">
          {/* Drop Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Drop Name *
            </label>
            <input
              id="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Sarah's Wedding"
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add details about your event..."
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          {/* Cover Photo */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Cover Photo
            </label>
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              <p className="text-muted-foreground">
                Drag and drop an image, or click to browse
              </p>
            </div>
          </div>

          {/* Custom Thank You Message (Pro Feature) */}
          <div>
            <label htmlFor="thankYouMessage" className="block text-sm font-medium mb-2">
              <span className="flex items-center gap-2">
                Custom Thank You Message
                {isPaidTier ? (
                  <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Sparkles className="h-3 w-3" />
                    Pro
                  </span>
                ) : (
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Pro only</span>
                )}
              </span>
            </label>
            <textarea
              id="thankYouMessage"
              rows={2}
              value={customThankYouMessage}
              onChange={(e) => setCustomThankYouMessage(e.target.value)}
              placeholder="Thanks for the memories!"
              disabled={!isPaidTier}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                !isPaidTier ? 'bg-muted cursor-not-allowed opacity-60' : ''
              }`}
            />
            <p className="text-xs text-muted-foreground mt-1">
              {isPaidTier
                ? 'This message will be shown to contributors after they upload photos.'
                : 'Upgrade to Pro to customize the thank you message shown to contributors.'}
            </p>
          </div>

          {/* Custom Branding (Pro Feature) */}
          <div className="border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Palette className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">Custom Branding</h3>
              {isPaidTier ? (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Sparkles className="h-3 w-3" />
                  Pro
                </span>
              ) : (
                <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Pro only</span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              {isPaidTier
                ? 'Customize the look of your contributor page with your brand colors.'
                : 'Upgrade to Pro to add custom branding to your contributor page.'}
            </p>
            <div className="grid grid-cols-2 gap-4">
              {/* Primary Color */}
              <div>
                <label htmlFor="primaryColor" className="block text-sm font-medium mb-2">
                  Primary Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="primaryColor"
                    type="color"
                    value={primaryColor || '#0ea5e9'}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    disabled={!isPaidTier}
                    className={`w-10 h-10 rounded border cursor-pointer ${
                      !isPaidTier ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    placeholder="#0ea5e9"
                    disabled={!isPaidTier}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      !isPaidTier ? 'bg-muted cursor-not-allowed opacity-60' : ''
                    }`}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Buttons and links</p>
              </div>

              {/* Accent Color */}
              <div>
                <label htmlFor="accentColor" className="block text-sm font-medium mb-2">
                  Accent Color
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="accentColor"
                    type="color"
                    value={accentColor || '#f97316'}
                    onChange={(e) => setAccentColor(e.target.value)}
                    disabled={!isPaidTier}
                    className={`w-10 h-10 rounded border cursor-pointer ${
                      !isPaidTier ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  />
                  <input
                    type="text"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    placeholder="#f97316"
                    disabled={!isPaidTier}
                    className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                      !isPaidTier ? 'bg-muted cursor-not-allowed opacity-60' : ''
                    }`}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">Highlights and progress</p>
              </div>
            </div>

            {/* Custom Logo URL */}
            <div className="mt-4">
              <label htmlFor="logoUrl" className="block text-sm font-medium mb-2">
                Custom Logo URL
              </label>
              <input
                id="logoUrl"
                type="url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                disabled={!isPaidTier}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary ${
                  !isPaidTier ? 'bg-muted cursor-not-allowed opacity-60' : ''
                }`}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Replaces "Photodrop" branding on contributor page. Recommended size: 200x60px
              </p>
              {isPaidTier && logoUrl && (
                <div className="mt-2 p-2 border rounded bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-2">Logo preview:</p>
                  <img
                    src={logoUrl}
                    alt="Custom logo preview"
                    className="h-10 max-w-[200px] object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none'
                    }}
                  />
                </div>
              )}
            </div>

            {/* Preview */}
            {isPaidTier && (primaryColor || accentColor || logoUrl) && (
              <div className="mt-4 p-4 border rounded-lg bg-muted/30">
                <p className="text-sm font-medium mb-2">Preview</p>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-md text-white text-sm font-medium"
                    style={{ backgroundColor: primaryColor || '#0ea5e9' }}
                  >
                    Upload Photos
                  </button>
                  <span className="text-sm" style={{ color: primaryColor || '#0ea5e9' }}>
                    Link text
                  </span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: '60%', backgroundColor: accentColor || '#f97316' }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-card text-card-foreground rounded-lg border border-destructive/50 p-6">
          <h3 className="text-lg font-semibold text-destructive">Danger Zone</h3>
          <p className="text-sm text-muted-foreground mt-2">
            These actions cannot be undone.
          </p>
          <div className="mt-4 flex gap-4">
            {drop?.status === 'active' && (
              <Button
                type="button"
                variant="outline"
                className="text-destructive border-destructive hover:bg-destructive/10"
                onClick={() => setShowCloseModal(true)}
              >
                <Lock className="mr-2 h-4 w-4" />
                Close Drop
              </Button>
            )}
            <Button
              type="button"
              variant="destructive"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Drop
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit">Save Changes</Button>
        </div>
      </form>

      {/* Close Drop Modal */}
      {showCloseModal && (
        <CloseDropModal
          dropName={dropName}
          onConfirm={handleCloseDrop}
          onCancel={() => setShowCloseModal(false)}
        />
      )}

      {/* Delete Drop Modal */}
      {showDeleteModal && (
        <DeleteDropModal
          dropName={dropName}
          onConfirm={handleDeleteDrop}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  )
}

function RealEditDropPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { drop, isLoading: loading, error, refetch } = useDropQuery(id)
  const updateDropMutation = useUpdateDropMutation()
  const deleteDropMutation = useDeleteDropMutation()

  const { proPlugin, exifPlugin } = useDropPluginsQuery(id)
  const upsertPluginMutation = useUpsertDropPluginMutation()
  const deletePluginMutation = useDeleteDropPluginMutation()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [pluginSettings, setPluginSettings] = useState<PluginSettings>({})
  const [exifSettings, setExifSettings] = useState<ExifExtractorSettings>({})

  const { organization } = useAuth()
  const tier = organization?.tier || 'free'
  const isPaidTier = tier === 'pro' || tier === 'business'

  useEffect(() => {
    if (!drop) return
    setName(drop.name)
    setDescription(drop.description || '')
  }, [drop])

  useEffect(() => {
    if (proPlugin) {
      setPluginSettings(proPlugin.settings)
    }
  }, [proPlugin])

  useEffect(() => {
    if (exifPlugin) {
      setExifSettings(exifPlugin.settings as ExifExtractorSettings)
    }
  }, [exifPlugin])

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!id) return

    try {
      await updateDropMutation.mutateAsync({
        id,
        set: {
          name: name.trim(),
          description: description.trim() || null,
        },
      })

      // Save plugin settings for paid tiers
      if (isPaidTier) {
        const hasSettings = Object.keys(pluginSettings).length > 0
        if (hasSettings) {
          await upsertPluginMutation.mutateAsync({ dropId: id, pluginId: 'com.photodrop.pro-image-processor', settings: pluginSettings })
        } else if (proPlugin) {
          // Settings cleared — remove the plugin config
          await deletePluginMutation.mutateAsync({ dropId: id, pluginId: 'com.photodrop.pro-image-processor' })
        }

        // Save EXIF settings for paid tiers (always upsert — empty settings = defaults)
        await upsertPluginMutation.mutateAsync({
          dropId: id,
          pluginId: 'com.photodrop.exif-extractor',
          settings: exifSettings,
        })
      }

      toast({ title: 'Saved', description: 'Drop updated.' })
      navigate(`/drops/${id}`)
    } catch (err) {
      toast({
        title: 'Failed to save',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  const onDelete = async () => {
    if (!id) return
    try {
      await deleteDropMutation.mutateAsync(id)
      toast({ title: 'Deleted', description: 'Drop deleted.' })
      navigate('/drops')
    } catch (err) {
      toast({
        title: 'Failed to delete',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      })
    }
  }

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading drop...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-semibold">Failed to load drop</h2>
          <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
          <div className="mt-4">
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    )
  }

  if (!drop) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-semibold">Drop not found</h2>
          <p className="mt-2 text-sm text-muted-foreground">This drop may have been deleted or you may not have access.</p>
          <div className="mt-4">
            <Button asChild variant="outline">
              <Link to="/drops">Back to drops</Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <Breadcrumb
        items={[
          { label: 'Drops', href: '/drops' },
          { label: drop.name, href: `/drops/${drop.id}` },
          { label: 'Edit' },
        ]}
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Edit Drop</h1>
          <p className="text-muted-foreground">Update your drop settings</p>
        </div>
        <Button variant="destructive" onClick={onDelete} disabled={deleteDropMutation.isPending}>
          <Trash2 className="mr-2 h-4 w-4" />
          {deleteDropMutation.isPending ? 'Deleting...' : 'Delete'}
        </Button>
      </div>

      <form onSubmit={onSave} className="space-y-6">
        <div className="bg-card text-card-foreground rounded-lg border p-6 space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Drop Name *
            </label>
            <input
              id="name"
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-2">
              Description
            </label>
            <textarea
              id="description"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {isPaidTier && (
          <PhotoSettings
            processingSettings={pluginSettings}
            exifSettings={exifSettings}
            onProcessingChange={setPluginSettings}
            onExifChange={setExifSettings}
            disabled={updateDropMutation.isPending}
          />
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={updateDropMutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" disabled={updateDropMutation.isPending}>
            {updateDropMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  )
}

interface CloseDropModalProps {
  dropName: string
  onConfirm: () => void
  onCancel: () => void
}

function CloseDropModal({ dropName, onConfirm, onCancel }: CloseDropModalProps) {
  const [isClosing, setIsClosing] = useState(false)

  const handleConfirm = async () => {
    setIsClosing(true)
    await onConfirm()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg border shadow-lg max-w-md w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onCancel}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 text-orange-600 mb-4">
          <Lock className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Close Drop</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Are you sure you want to close <span className="font-medium text-foreground">"{dropName}"</span>?
        </p>

        <p className="text-sm text-muted-foreground mb-6">
          Contributors will no longer be able to upload photos. This action cannot be undone.
        </p>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            disabled={isClosing}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1 bg-orange-600 hover:bg-orange-700"
            onClick={handleConfirm}
            disabled={isClosing}
          >
            {isClosing ? 'Closing...' : 'Close Drop'}
          </Button>
        </div>
      </div>
    </div>
  )
}

interface DeleteDropModalProps {
  dropName: string
  onConfirm: () => void
  onCancel: () => void
}

function DeleteDropModal({ dropName, onConfirm, onCancel }: DeleteDropModalProps) {
  const [isDeleting, setIsDeleting] = useState(false)

  const handleConfirm = async () => {
    setIsDeleting(true)
    await onConfirm()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50"
        onClick={onCancel}
      />

      {/* Modal */}
      <div className="relative bg-card text-card-foreground rounded-lg border shadow-lg max-w-md w-full mx-4 p-6 z-10">
        <button
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          onClick={onCancel}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3 text-destructive mb-4">
          <AlertTriangle className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Delete Drop</h2>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Are you sure you want to delete <span className="font-medium text-foreground">"{dropName}"</span>?
        </p>

        <p className="text-sm text-muted-foreground mb-6">
          This action cannot be undone. All photos and contributor data will be permanently deleted.
        </p>

        {/* Action buttons */}
        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            onClick={handleConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete Drop'}
          </Button>
        </div>
      </div>
    </div>
  )
}
